import {
    InsuranceCompanySeed,
    DepartmentInfo,
    DepartmentBadgeUIConfig,
    InsuranceCompanyBexioInfo
} from '../../shared/models/domain/InsuranceCompanySeed';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ShellDomainProvider, UtilityService } from '../../shared/standalone-packages/ecap3/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GqlQueryCommandService } from '../../shared/services/gql-query-command.service';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { CommonService } from '../../shared/services/common.service';
import { SeedDataDrawerModal } from '../components/Model/SeedMetaDataModel';
import { ComponentSeedDrawerModel } from '../components/Model/ComponentSeedDrawerModel';
import { SubItemConfiguration } from '../components/Model/SubItemConfiguration';
import { IgnoredSubItemText } from '../components/Model/IgnoredSubItemText';
import { GetGenericEntityDataQuery } from '../../shared/models/queryModels/GetGenericEntityDataQuery';
import { QueryHandlerResponse } from '../../shared/models/responseModels/QueryHandlerResponse';
import { StoreDataService } from '../../shared/services/store-data.service';
import { ServiceProductPrice } from '../../shared/models/domain/ServiceProductPrice';
import { IpexCommandResponse } from '../../shared/models/responseModels/IpexCommandResponse';
import { GetTestingCategoriesQuery } from '../../shared/models/queryModels/GetTestingCategoriesQuery';
import { TestingCategoryModel } from '../../shared/models/TestingCategoryModel';
import { TestingCategoryCRUDDrawerModel } from '../components/Model/TestingCategoryCRUDDrawerModel';
import { SeedMetaData } from '../../shared/models/domain/SeedMetaData';
import { TestingCategoryInfo } from '../../shared/models/domain/TestingCategoryInfo';
import { PCXKeyValue } from '../../shared/models/domain/PCXKeyValue';
import { UnitInfo } from '../../shared/models/domain/UnitInfo';
import { CaseUnit } from '../../shared/models/domain/CaseUnit';
import { InsertOrUpdateTestingCategoryCommand } from '../../shared/models/commandModels/InsertOrUpdateTestingCategoryCommand';
import { DrawerModel } from '../components/Model/DrawerModel';
import { Vat } from '../../shared/models/domain/Vat';
import { DatatableModel } from '../../shared/standalone-packages/ecap3/app-platform-datatable';
import { ValidationService } from '../../shared/standalone-packages/ecap3/app-platform-front-end-validation';
import { BexioContactInfo } from '../../shared/models/domain/BexioContactInfo';
import { GetBexioContactInfoQuery } from '../../shared/models/queryModels/GetBexioContactInfoQuery';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class DefaultsService {
    private readonly urlResolver = inject(UrlResolverService);
    languages: PCXKeyValue[] = SCConfig.Languages;
    unitInfos: UnitInfo[];
    caseUnitSeedData = new BehaviorSubject<any>(null);
    insuranceCompanySeed = new BehaviorSubject<any>(null);
    seedDataModel = new BehaviorSubject<any>(null);
    isEditFromDetails = new BehaviorSubject<boolean>(false);
    drawerDataModel = new BehaviorSubject<SeedDataDrawerModal>(null);
    testingCategoryDrawerModel = new BehaviorSubject<TestingCategoryCRUDDrawerModel>(null);
    componentSeedDrawerModel = new BehaviorSubject<ComponentSeedDrawerModel>(null);
    vatDrawerModel = new BehaviorSubject<DrawerModel<Vat>>(null);
    private bexioAmountChangeFormComponentData = new BehaviorSubject<InsuranceCompanyBexioInfo>(null);
    bexioAmountChangeFormComponentData$ = this.bexioAmountChangeFormComponentData.asObservable();

    processCaseSeedUrl = `${this.urlResolver.getPcxCommandUrl()}ProcessCaseUnit`;
    processInsuranceCompanyUrl = `${this.urlResolver.getPcxCommandUrl()}ProcessInsuranceCompany`;
    getSeedDataUrl = `${this.urlResolver.getPcxQueryUrl()}GetCaseUnit`;
    pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    pcxCommandUrl: string = this.urlResolver.getPcxCommandUrl();
    externalBexioUrl: string = this.urlResolver.getExternalBexioUrl();
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    private commonOptions: any = { headers: this.header, withCredentials: true, observe: 'response' };
    private serviceProductPricesSubject: BehaviorSubject<ServiceProductPrice[]> = new BehaviorSubject<
        ServiceProductPrice[]
    >([]);
    public serviceProductPrices$: Observable<ServiceProductPrice[]> = this.serviceProductPricesSubject.asObservable();

    private serviceProductPriceModalShowLoaderSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public serviceProductPriceModalShowLoader$: Observable<boolean> =
        this.serviceProductPriceModalShowLoaderSubject.asObservable();

    constructor(
        private http: HttpClient,
        private gqlQueryCommandService: GqlQueryCommandService,
        private formBuilder: FormBuilder,
        private commonService: CommonService,
        private utilityService: UtilityService
    ) {}

    initializemptyData() {
        this.unitInfos = [];
        this.languages.forEach((lang: PCXKeyValue) => {
            this.unitInfos.push({
                UnitLanguage: lang.Key,
                UnitName: {
                    Key: '',
                    Value: ''
                },
                UnitTags: ['']
            });
        });
        return this.unitInfos;
    }

    createForm(ItemId: string, unitInfos: UnitInfo[]) {
        return this.formBuilder.group({
            ItemId: [ItemId],
            UnitInfos: this.formBuilder.array(unitInfos.map(this.createUnitInfoForm.bind(this)))
        });
    }

    createUnitInfoForm(unitInfo: UnitInfo) {
        return this.formBuilder.group({
            UnitLanguage: [unitInfo.UnitLanguage],
            UnitTags: [unitInfo.UnitTags.length < 1 ? [''] : unitInfo.UnitTags],
            UnitName: this.createPcxKeyValueForm(unitInfo.UnitName)
        });
    }

    createInsuranceCompanyForm(insuranceCompany?: InsuranceCompanySeed) {
        let deptInfos: DepartmentInfo[] = [];
        if (insuranceCompany && insuranceCompany.DepartmentInfoList) {
            deptInfos = insuranceCompany.DepartmentInfoList;
        }
        return this.formBuilder.group({
            ItemId: [
                (insuranceCompany && insuranceCompany.ItemId) || this.commonService.getNewGuid(),
                Validators.required
            ],
            Name: [insuranceCompany && insuranceCompany.Name, Validators.required],
            LogoFileId: [insuranceCompany && insuranceCompany.LogoFileId],
            BexioContactInfo: [null],
            CompanyType: [
                (insuranceCompany && insuranceCompany.CompanyType) || 'insurance_provider',
                Validators.required
            ],
            SlaDaysOfDelay: [
                insuranceCompany && insuranceCompany.SlaDaysOfDelay,
                [Validators.required, this.rangeValidator(1, 10)]
            ],
            IsLiabilityEvaluationMinimumThresholdPercentageEnabled: [
                insuranceCompany?.IsLiabilityEvaluationMinimumThresholdPercentageEnabled ?? false
            ],
            LiabilityEvaluationMinimumThresholdPercentage: [
                {
                    value: insuranceCompany?.LiabilityEvaluationMinimumThresholdPercentage ?? 20,
                    disabled: insuranceCompany?.IsLiabilityEvaluationMinimumThresholdPercentageEnabled ?? false
                },
                [Validators.min(0), Validators.max(100)]
            ]
        });
    }

    createPcxKeyValueForm(unitName: PCXKeyValue) {
        return this.formBuilder.group({
            Key: [unitName.Key, Validators.required],
            Value: [unitName.Value, Validators.required]
        });
    }
    createBexioAmountChangeForm(modal: InsuranceCompanyBexioInfo) {
        return this.formBuilder.group({
            CurrentAmount: [
                { value: modal.BexioCurrentAmount === null ? 0 : modal.BexioCurrentAmount, disabled: true }
            ],
            NextAmount: [modal.BexioNextAmount === null ? 0 : modal.BexioNextAmount],
            EffectiveDate: [modal.BexioEffectiveDate]
        });
    }
    getColumnConfig() {
        return;
    }

    getCaseUnitSeedData(dataTableModel: DatatableModel<CaseUnit>) {
        const payload = {
            SortBy: `{${dataTableModel.SortBy}: ${dataTableModel.SortDir === 'asc' ? 1 : -1}}`,
            FilterString: `{}`,
            PageNumber: dataTableModel ? dataTableModel.CurrentPageNumber : 0,
            PageSize: dataTableModel ? dataTableModel.PageSize : 10
        };

        return this.http
            .post(this.getSeedDataUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }

    saveOrUpdateCaseUnit(payload: any) {
        return this.http
            .post(this.processCaseSeedUrl, payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    saveOrUpdateInsuranceCompany(payload: any) {
        return this.http
            .post(this.processInsuranceCompanyUrl, payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    getGenericEntityData(entityName: string, dataTableModel?: DatatableModel<any>, filterString?: string) {
        const payload = {
            EntityName: entityName,
            SortBy: `{${dataTableModel.SortBy}: ${dataTableModel.SortDir === 'asc' ? 1 : -1}}`,
            FilterString: filterString ? filterString : `{}`,
            PageNumber: dataTableModel ? dataTableModel.CurrentPageNumber : 0,
            PageSize: dataTableModel ? dataTableModel.PageSize : 10,
            UsePagination: true
        };

        return this.http
            .post(this.pcxQueryUrl + 'GetGenericEntityData', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }

    public getGenericEntityDataParsed<Type>(
        requestModel: GetGenericEntityDataQuery
    ): Observable<QueryHandlerResponse<Type, null>> {
        return this.http
            .post<QueryHandlerResponse<string, null>>(this.pcxQueryUrl + 'GetGenericEntityData', requestModel, {
                headers: this.header,
                withCredentials: true
            })
            .pipe(
                map(response => {
                    let newResponse: QueryHandlerResponse<Type, null> = new QueryHandlerResponse<Type, null>();
                    newResponse.ErrorMessage = response.ErrorMessage;
                    newResponse.Results = response.Results;
                    newResponse.StatusCode = response.StatusCode;
                    newResponse.TotalCount = response.TotalCount;
                    newResponse.Data = StoreDataService.ParseJsonStringToJsonToType<Type>(response.Data);
                    return newResponse;
                })
            );
    }

    createOrUpdateHolidays(payload: any) {
        payload.StartDate = this.commonService.getDateToUTC(new Date(payload.StartDate), true);
        payload.EndDate = this.commonService.getDateToUTC(new Date(payload.EndDate), true);

        return this.http
            .post(this.pcxCommandUrl + 'ProcessHoliDays', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }
    createOrUpdateIgnoredSubItemText(ignoredSubItemText: IgnoredSubItemText) {
        ignoredSubItemText.IgnoredText = ignoredSubItemText.IgnoredText.trim();
        return this.http
            .post(this.pcxCommandUrl + 'ProcessIgnoredSubItemText', ignoredSubItemText, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }

    createOrUpdateSubItemNegativeConfiguration(subItemConfiguration: SubItemConfiguration) {
        subItemConfiguration.SubItemTitle = subItemConfiguration.SubItemTitle.trim();
        return this.http
            .post(this.pcxCommandUrl + 'ProcessSubItemConfiguration', subItemConfiguration, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }

    public createSeedMetaDataForm(data?: SeedMetaData) {
        let labels: PCXKeyValue[] = [];
        if (data === null || data === undefined) {
            this.languages.forEach((lang: PCXKeyValue) => {
                labels.push({
                    Key: lang.Key,
                    Value: ''
                });
            });
        } else {
            labels = data.Label;
        }
        return this.formBuilder.group({
            ItemId: [(data && data.ItemId) || this.utilityService.getNewGuid()],
            EntityName: [(data && data.EntityName) || ''],
            Key: [(data && data.Key) || ''],
            Value: [data && data.Value],
            Active: [(data && data.Active) || true],
            Label: this.formBuilder.array(labels.map(this.createSeedDataLabelForm.bind(this)))
        });
    }

    public createSeedDataLabelForm(item?: PCXKeyValue) {
        return this.formBuilder.group({
            Key: [item && item.Key],
            Value: [(item && item.Value) || '', Validators.required]
        });
    }

    public createOrUpdateGenericSeedMetaData(payload: any) {
        let url = this.pcxCommandUrl + 'InsertOrUpdateSeedMetaData';
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response.body;
            })
        );
    }

    public createTestingCategoryForm(data?: TestingCategoryModel) {
        return {
            SeedMetaDataForm: this.createSeedMetaDataForm(data?.SeedMetaData),
            TestingCategoryInfoForm: this.createTestingCategoryInfoForm(data?.TestingCategoryInfo)
        };
    }

    private createTestingCategoryInfoForm(data?: TestingCategoryInfo) {
        return this.formBuilder.group({
            ItemId: [(data && data.ItemId) || this.utilityService.getNewGuid()],
            TestingCategoryId: [(data && data.TestingCategoryId) || ''],
            LowestThresholdPercentage: [(data && data.LowestThresholdPercentage) || 0],
            LowestTolerancePercentage: [(data && data.LowestTolerancePercentage) || 0],
            HighestTolerancePercentage: [(data && data.HighestTolerancePercentage) || 0],
            HighestThresholdPercentage: [(data && data.HighestThresholdPercentage) || 0]
        });
    }

    /**
     * Gets testing categories along with TestingCategoryInfo
     * WARNING: ONLY use this if you need testing category along with TestingCategoryInfo
     * @param payload
     * @returns
     */
    public getTestingCategories(
        payload: GetTestingCategoriesQuery
    ): Observable<QueryHandlerResponse<null, TestingCategoryModel[]>> {
        let url = this.pcxQueryUrl + 'GetTestingCategory';
        return this.http.post<QueryHandlerResponse<null, TestingCategoryModel[]>>(url, payload, {
            headers: this.header,
            withCredentials: true
        });
    }

    /**
     * Gets testing categories along with TestingCategoryInfo
     * WARNING: ONLY use this if you need testing category along with TestingCategoryInfo
     * @returns All testing categories
     */
    public getAllTestingCategories(): Observable<QueryHandlerResponse<null, TestingCategoryModel[]>> {
        let payload: GetTestingCategoriesQuery = {
            FilterString: "{EntityName:'TestingCategorySeed' , IsMarkedToDelete: false}",
            PageNumber: 0,
            PageSize: 100000,
            SortBy: '{_id : 1}',
            UsePagination: false
        };
        return this.getTestingCategories(payload);
    }

    public insertOrUpdateTestingCategory(
        payload: InsertOrUpdateTestingCategoryCommand
    ): Observable<IpexCommandResponse<TestingCategoryModel>> {
        let url = this.pcxCommandUrl + 'InsertOrUpdateTestingCategory';
        return this.http.post<IpexCommandResponse<TestingCategoryModel>>(url, payload, {
            headers: this.header,
            withCredentials: true
        });
    }

    sendDataToBexioAmountChangeComponent(
        currentAmount: number | null,
        nextAmount: number | null,
        effectiveDate: string | null
    ): void {
        let insuranceCompanyBexioInfo = new InsuranceCompanyBexioInfo();
        insuranceCompanyBexioInfo.BexioCurrentAmount = currentAmount;
        insuranceCompanyBexioInfo.BexioNextAmount = nextAmount;
        insuranceCompanyBexioInfo.BexioEffectiveDate = effectiveDate;
        this.bexioAmountChangeFormComponentData.next(insuranceCompanyBexioInfo);
    }

    public setServiceProductPrices(serviceProductPrices: ServiceProductPrice[]): void {
        this.serviceProductPricesSubject.next(serviceProductPrices);
    }

    public setServiceProductPriceModalShowLoader(showLoader: boolean) {
        this.serviceProductPriceModalShowLoaderSubject.next(showLoader);
    }

    syncIpexMasterData(payload: any) {
        return this.http
            .post(this.pcxCommandUrl + 'ResyncIpexCentralService', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response.body;
                })
            );
    }

    public importInspectionReportExcel(fileId: string, Purpose: string): Observable<IpexCommandResponse<any>> {
        const url = this.pcxCommandUrl + 'ImportInspectionReportExcel';
        const payload = {
            FileId: fileId,
            Purpose
        };
        return this.http.post<IpexCommandResponse<any>>(url, payload, { headers: this.header, withCredentials: true });
    }

    public getInspectionReportExcel(Purpose: string): Observable<IpexCommandResponse<any>> {
        const url = this.pcxQueryUrl + 'GetInspectionReportExcel';
        const payload = {
            Purpose
        };
        return this.http.post<IpexCommandResponse<any>>(url, payload, { headers: this.header, withCredentials: true });
    }

    public toggleCompanyDepartmentStatus(payload: any): Observable<IpexCommandResponse<any>> {
        return this.http
            .post<IpexCommandResponse<any>>(this.pcxCommandUrl + 'ToggleCompanyDepartmentStatus', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response?.body;
                })
            );
    }

    public insertOrUpdateDepartmentInfo(payload: any): Observable<IpexCommandResponse<any>> {
        return this.http
            .post<IpexCommandResponse<any>>(this.pcxCommandUrl + 'InsertOrUpdateDepartmentInfo', payload, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(
                map((response: any) => {
                    return response?.body;
                })
            );
    }

    rangeValidator(min: number, max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            const floatPattern = /^(10|[1-9])$/;

            if (value == null || value === '') {
                return { required: true };
            }

            if (!floatPattern.test(value)) {
                return { invalidRange: true };
            }

            const numericValue = parseFloat(value);
            if (numericValue < min || numericValue > max) {
                return { invalidRange: true };
            }
            return null;
        };
    }

    public getBexioContactList(
        payload: GetBexioContactInfoQuery
    ): Observable<QueryHandlerResponse<null, BexioContactInfo[]>> {
        let url = this.externalBexioUrl + 'GetBexioContactInfo';
        return this.http.post<QueryHandlerResponse<null, BexioContactInfo[]>>(url, payload, {
            headers: this.header,
            withCredentials: true
        });
    }

    public reprocessFailedDocuments(payload: any): Observable<IpexCommandResponse<any>> {
        const url = this.pcxCommandUrl + 'ReprocessFailedDocuments';
        return this.http.post<IpexCommandResponse<any>>(url, payload, {
            headers: this.header,
            withCredentials: true
        });
    }
}
